import React, { useState } from 'react';
import { Platform, View } from 'react-native';
import { CURRENT_USER } from '@ventureroom/core';
import { useApp, useData, useMe } from './provider';
import {
  Avatar,
  Button,
  Card,
  Column,
  Copy,
  Empty,
  FormSheet,
  Heading,
  Page,
  Row,
  Sheet,
  Tag,
  dateLabel,
} from './components';
export function Activity() {
  const data = useData();
  const { navigate, act } = useApp();
  const invitations = data.invitations.filter(
    (i) => i.to === CURRENT_USER && i.status === 'pending',
  );
  const items = data.activities
    .filter(
      (a) =>
        !a.ventureId ||
        data.ventures.some((v) => v.id === a.ventureId && v.members.includes(CURRENT_USER)),
    )
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  return (
    <Page title="Activity">
      {invitations.map((i) => (
        <Card key={i.id}>
          <Tag>{i.kind === 'request' ? 'Join request' : 'Venture invitation'}</Tag>
          <Heading size={20}>{data.ventures.find((v) => v.id === i.ventureId)?.name}</Heading>
          <Copy>
            {data.profiles.find((p) => p.id === i.from)?.name}{' '}
            {i.kind === 'request' ? 'would like to join your team.' : 'invited you to the team.'}
          </Copy>
          <Row>
            <Button
              onPress={() =>
                void act(
                  { type: 'invitationRespond', id: i.id, accept: true },
                  'Venture invitation accepted',
                )
              }
            >
              Accept
            </Button>
            <Button
              kind="quiet"
              onPress={() =>
                void act(
                  { type: 'invitationRespond', id: i.id, accept: false },
                  'Invitation declined',
                )
              }
            >
              Decline
            </Button>
          </Row>
        </Card>
      ))}
      {items.length ? (
        items.map((a) => (
          <Card
            key={a.id}
            onPress={() =>
              navigate(
                a.ventureId
                  ? `venture/${a.ventureId}`
                  : a.kind === 'connection'
                    ? 'connections'
                    : 'profile',
              )
            }
          >
            <Row between>
              <Tag>{a.kind}</Tag>
              <Copy muted size={12}>
                {dateLabel(a.createdAt)}
              </Copy>
            </Row>
            <Heading size={19}>{a.title}</Heading>
            <Copy muted size={13}>
              {a.detail}
            </Copy>
          </Card>
        ))
      ) : (
        <Empty
          title="Your next chapter starts here"
          detail="Connections, decisions, and team progress will appear in this timeline."
        />
      )}
    </Page>
  );
}
export function Settings() {
  const app = useApp();
  const me = useMe();
  const [pair, setPair] = useState(false),
    [recoveryEmail, setRecoveryEmail] = useState(false),
    [confirmReset, setConfirmReset] = useState(false),
    [confirmDelete, setConfirmDelete] = useState(false),
    [info, setInfo] = useState<string | null>(null);
  return (
    <Page title="Settings" back="profile">
      {app.authenticated && (
        <Button kind="quiet" onPress={() => void app.logout()}>
          Sign out
        </Button>
      )}
      {app.authenticated && app.needsRecoveryEmail && (
        <Column gap={8}>
          <Heading size={20}>Email recovery</Heading>
          <Copy muted size={13}>
            Accounts created before email verification can add a recovery email here. New accounts
            already have one.
          </Copy>
          <Row>
            <Button kind="secondary" onPress={() => setRecoveryEmail(true)}>
              Add recovery email
            </Button>
          </Row>
        </Column>
      )}
      {recoveryEmail && (
        <FormSheet
          title="Add recovery email"
          fields={[
            { key: 'email', label: 'Recovery email', required: true },
            { key: 'password', label: 'Current password', type: 'secret', required: true },
          ]}
          initial={{ email: '', password: '' }}
          onClose={() => setRecoveryEmail(false)}
          submit="Send verification email"
          onSave={async (values) => {
            const result = await app.accountRequest('recovery-email', values);
            if (result) app.announce(result.message ?? 'Check your email.');
            return !!result;
          }}
        />
      )}
      <Card>
        <Heading size={20}>Appearance</Heading>
        <Row wrap>
          <Button
            kind={app.theme === 'warm' ? 'primary' : 'secondary'}
            onPress={() => void app.setTheme('warm')}
          >
            ☀ Light Mode
          </Button>
          <Button
            kind={app.theme === 'dark' ? 'primary' : 'secondary'}
            onPress={() => void app.setTheme('dark')}
          >
            ◐ Dark Mode
          </Button>
        </Row>
      </Card>
      <Card>
        <Heading size={20}>Accessibility</Heading>
        <Row between wrap>
          <Column gap={4}>
            <Copy bold>Reduce motion & haptics</Copy>
            <Copy muted size={13}>
              Keep transitions and feedback quiet.
            </Copy>
          </Column>
          <Button kind="secondary" onPress={() => void app.setReduceMotion(!app.reduceMotion)}>
            {app.reduceMotion ? 'On' : 'Off'}
          </Button>
        </Row>
      </Card>
      <Card>
        <Heading size={20}>Collaboration preferences</Heading>
        <Row between wrap>
          <Copy>Open to new collaborators</Copy>
          <Button
            kind="secondary"
            onPress={() =>
              void app.act(
                { type: 'profile', profile: { ...me, looking: !me.looking } },
                'Discovery preference updated',
              )
            }
          >
            {me.looking ? 'Open' : 'Taking a pause'}
          </Button>
        </Row>
      </Card>
      {!app.authenticated && (
        <Card>
          <Row between>
            <Heading size={20}>Local development</Heading>
            <Tag>{app.mode === 'server' ? 'VM connected' : 'Offline workspace'}</Tag>
          </Row>
          <Copy muted>
            Offline mode stays on this device. Connecting to the VM opens its shared workspace. The
            two datasets stay separate; switching does not upload or overwrite your offline work.
          </Copy>
          <Row wrap>
            {app.mode === 'server' ? (
              <>
                <Button kind="secondary" onPress={() => void app.refresh()}>
                  Refresh from backend
                </Button>
                <Button kind="quiet" onPress={() => void app.disconnect()}>
                  Use offline workspace
                </Button>
              </>
            ) : (
              <Button kind="secondary" onPress={() => setPair(true)}>
                Connect local backend
              </Button>
            )}
            <Button
              kind="quiet"
              disabled={app.mode === 'server'}
              onPress={() => setConfirmReset(true)}
            >
              Reset demo data
            </Button>
          </Row>
        </Card>
      )}
      {!app.authenticated && (
        <Card>
          <Heading size={20}>Account management</Heading>
          <Row between wrap>
            <Column gap={4}>
              <Copy bold>Delete account</Copy>
              <Copy muted size={13}>
                Permanently remove your profile and data.
              </Copy>
            </Column>
            <Button kind="danger" onPress={() => setConfirmDelete(true)}>
              Delete account
            </Button>
          </Row>
        </Card>
      )}
      <Card>
        <Heading size={20}>Good to know</Heading>
        {['Privacy & storage', 'Notifications', 'Account', 'About VentureRoom'].map((label) => (
          <Row between key={label}>
            <Copy>{label}</Copy>
            <Button label={`Open ${label}`} kind="quiet" onPress={() => setInfo(label)}>
              Read →
            </Button>
          </Row>
        ))}
      </Card>
      {pair && (
        <FormSheet
          title="Connect your local backend"
          fields={[
            { key: 'url', label: 'Backend URL', required: true },
            {
              key: 'token',
              label: 'Local pairing token',
              type: 'secret',
              hint: 'Stored securely on mobile; in this browser session on web.',
              required: true,
            },
          ]}
          initial={{
            url: Platform.OS === 'android' ? 'http://10.0.2.2:8787' : 'http://127.0.0.1:8787',
            token: '',
          }}
          onClose={() => setPair(false)}
          submit="Connect"
          onSave={(v) => app.pair(String(v.url), String(v.token))}
        />
      )}
      {confirmReset && (
        <Sheet title="Start the demo afresh?" onClose={() => setConfirmReset(false)}>
          <Copy>
            This replaces this device’s offline founder profiles, messages, and venture records with
            fictional examples. The backend workspace is unaffected.
          </Copy>
          <Button
            kind="danger"
            onPress={async () => {
              void app.reset();
              setConfirmReset(false);
            }}
          >
            Reset offline demo
          </Button>
          <Button kind="quiet" onPress={() => setConfirmReset(false)}>
            Keep my changes
          </Button>
        </Sheet>
      )}
      {confirmDelete && (
        <Sheet title="Delete your account?" onClose={() => setConfirmDelete(false)}>
          <Copy>
            This will permanently remove your profile, ventures, and all associated data. This
            action cannot be undone.
          </Copy>
          <Button
            kind="danger"
            onPress={async () => {
              void app.reset();
              setConfirmDelete(false);
            }}
          >
            Yes, delete account
          </Button>
          <Button kind="quiet" onPress={() => setConfirmDelete(false)}>
            Cancel
          </Button>
        </Sheet>
      )}
      {info && (
        <Sheet title={info} onClose={() => setInfo(null)}>
          {info === 'Privacy & storage' ? (
            <Copy>
              Offline records stay in this device’s local database. In VM mode, records are sent to
              your local backend. There are no analytics, external photos, or tracking services.
              Demo reset erases offline changes. Browser storage can also be cleared through your
              browser settings. Accounts use scoped access and secure sessions. Your chats and
              project messages stay with their participants.
            </Copy>
          ) : info === 'Notifications' ? (
            <Copy>
              Your Activity tab brings together invitations and team progress. This beta does not
              send push notifications or emails. Open the app to catch up; calendar integration can
              be added later.
            </Copy>
          ) : info === 'Account' ? (
            <Column>
              <Avatar person={me} />
              <Heading size={20}>{me.name}</Heading>
              <Copy>
                Sign in with your own username and password. Your account has its own chats and
                project access. Use Sign out to revoke this device’s session.
              </Copy>
            </Column>
          ) : (
            <Column>
              <Heading size={24}>VentureRoom</Heading>
              <Copy>
                Version 0.1.0 · A place for complementary people to build businesses together.
              </Copy>
              <Copy muted>
                Founder matching and a lightweight team workspace. No payments, investment listings,
                wallets, or financial return calculations.
              </Copy>
              <Copy muted size={12}>
                Temporary original brand assets. All seeded people are fictional.
              </Copy>
            </Column>
          )}
        </Sheet>
      )}
      <Card>
        <Heading size={20}>AI collaborators</Heading>
        <Copy muted>
          Connect the local backend to work with AI specialists. Model credentials are configured on
          the server.
        </Copy>
      </Card>
      <SubscriptionManager />
      
    </Page>
  );
}

function SubscriptionManager() {
  const app = useApp();
  const me = useMe();
  const unifiedPlans = [
    {
      id: 'free',
      name: 'Free',
      price: '£0/mo',
      features: ['5 active ventures', 'Collaborators up to 3/5 money bags', 'Basic AI chat'],
    },
    {
      id: 'basic',
      name: 'Basic',
      price: '£9.99/mo',
      features: [
        '10 active ventures',
        'Collaborators up to 4/5 money bags',
        '5,000 AI words/mo',
        'Basic support'
      ]
    },
    {
      id: 'starter',
      name: 'Starter',
      price: '£24.99/mo',
      features: [
        'Unlimited ventures',
        'Collaborators up to 4/5 money bags',
        '16,500 AI words/mo',
        'Basic Analytics',
        'Minor Priority Matching',
        'Priority support',
      ],
    },
    {
      id: 'pro',
      name: 'Pro',
      price: '£49.99/mo',
      features: [
        'Unlimited ventures',
        'Collaborators up to 5/5 money bags',
        'Website Stats Dashboard',
        '33,750 AI words/mo',
        'Advanced Analytics',
        'Major Priority Matching',
        'Team Digest',
        'Priority support',
      ],
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      price: '£149.99/mo',
      features: [
        'Unlimited ventures',
        'Collaborators up to 5/5 money bags',
        'Website Stats Dashboard',
        '60,000 AI words/mo',
        'Advanced Analytics',
        'Max Visibility',
        'Verified Badge',
        'Ability to create your own team',
        'Team Digest & Priority support',
        'Document Uploads',
        'The AI Boardroom',
      ],
    },
    {
      id: 'ultimate',
      name: 'Ultimate',
      price: '£199.99/mo',
      features: [
        'Unlimited ventures',
        'Collaborators up to 5/5 money bags',
        'Website Stats Dashboard',
        '100,000 AI words/mo',
        'Advanced Analytics',
        'Max Visibility',
        'Verified Badge',
        'Ability to create your own team',
        'Team Digest & Priority support',
        'Document Uploads',
        'The AI Boardroom',
        'Dedicated Account Manager'
      ],
    }
  ];

  const plans = unifiedPlans;
  const currentTier = me.subscriptionTier || 'free';

  return (
    <Card>
      <Heading size={20}>Subscription Plan</Heading>
      <Copy muted size={13}>
        Free includes five active ventures and collaborators rated up to three money emojis. Paid
        plans unlock higher ratings and unlimited ventures. Prices below are proposed; checkout is
        not available yet.
      </Copy>
      <Column gap={12} style={{ marginTop: 16 }}>
        {plans.map((plan) => (
          <View
            key={plan.id}
            style={{
              padding: 16,
              borderWidth: currentTier === plan.id ? 2 : 1,
              borderColor: currentTier === plan.id ? '#007bff' : '#ddd',
              borderRadius: 8,
              backgroundColor: currentTier === plan.id ? 'rgba(0, 123, 255, 0.05)' : 'transparent',
            }}
          >
            <Row between style={{ alignItems: 'center' }}>
              <Column style={{ flex: 1, paddingRight: 16 }}>
                <Heading size={16}>
                  {plan.name} <Copy muted>{plan.price}</Copy>
                </Heading>
                <Copy size={13} muted>
                  {plan.features.join(' - ')}
                </Copy>
              </Column>
              <Button
                kind={currentTier === plan.id ? 'primary' : 'secondary'}
                disabled={currentTier === plan.id || app.authenticated}
                onPress={() => {
                  if (!app.authenticated)
                    app.act(
                      {
                        type: 'subscribe',
                        tier: plan.id as NonNullable<typeof me.subscriptionTier>,
                      },
                      `Demo plan changed to ${plan.name}.`,
                    );
                }}
              >
                {currentTier === plan.id
                  ? 'Current Plan'
                  : app.authenticated
                    ? 'Coming soon'
                    : 'Try in demo'}
              </Button>
            </Row>
          </View>
        ))}
      </Column>
      {app.authenticated && <Copy muted>Paid upgrades are not available to purchase yet.</Copy>}
    </Card>
  );
}
